import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  DollarSign, RefreshCcw, Clock, FileDown, FileSpreadsheet, CheckCircle2,
  XCircle, ThumbsUp, ExternalLink, Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_LABELS = {
  pendente:  { label: "PENDENTE",  className: "bg-amber-100 text-amber-700 border-amber-300" },
  aprovada:  { label: "APROVADA",  className: "bg-sky-100 text-sky-700 border-sky-300" },
  paga:      { label: "PAGA",      className: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  cancelada: { label: "CANCELADA", className: "bg-red-100 text-red-700 border-red-300" },
};

const brl = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Componente de Comissões Pós-venda (3%).
 * Pode ser usado como página standalone OU embarcado dentro de uma aba.
 *
 * Props:
 *   - embedded: boolean — quando true, não renderiza header próprio (útil em Tabs).
 */
export default function PosvendaComissoes({ embedded = false }) {
  const { user } = useAuth();
  const role = user?.role;
  const isAdminViewer = ["diretor", "gerente", "supervisao", "financeiro"].includes(role);
  const canAprovar = ["diretor", "gerente", "financeiro"].includes(role);
  const canPagar = ["diretor", "financeiro"].includes(role);
  const canCancelar = role === "diretor";

  const [comissoes, setComissoes] = useState([]);
  const [kpis, setKpis] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, acao: null, comissao: null });
  const [observacao, setObservacao] = useState("");
  const [saving, setSaving] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      params.limit = 500;
      const r = await api.get(`/comissoes`, { params });
      setComissoes(r.data.items || []);
      setKpis(r.data.kpis || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar comissões");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!search) return comissoes;
    const q = search.toLowerCase();
    return comissoes.filter((c) =>
      (c.os_numero || "").toLowerCase().includes(q) ||
      (c.proposta_numero || "").toLowerCase().includes(q) ||
      (c.vendedor_nome || "").toLowerCase().includes(q) ||
      (c.cliente_nome || "").toLowerCase().includes(q),
    );
  }, [search, comissoes]);

  const abrirAcao = (acao, comissao) => {
    setModal({ open: true, acao, comissao });
    setObservacao("");
  };

  const confirmar = async () => {
    const { acao, comissao } = modal;
    setSaving(true);
    try {
      if (acao === "aprovar") {
        await api.patch(`/comissoes/${comissao.id}/aprovar`, { observacao });
        toast.success("Comissão aprovada");
      } else if (acao === "pagar") {
        await api.patch(`/comissoes/${comissao.id}/pagar`, { observacao });
        toast.success("Comissão paga");
      } else if (acao === "cancelar") {
        await api.delete(`/comissoes/${comissao.id}`);
        toast.success("Comissão cancelada");
      }
      setModal({ open: false, acao: null, comissao: null });
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    } finally {
      setSaving(false);
    }
  };

  const exportar = async (formato) => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const r = await api.get(`/comissoes/exportar/${formato}`, { params, responseType: "blob" });
      const blob = new Blob([r.data], {
        type: formato === "pdf" ? "application/pdf" : "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `comissoes_${new Date().toISOString().slice(0, 10)}.${formato}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportado em ${formato.toUpperCase()}`);
    } catch (err) {
      toast.error(`Falha na exportação ${formato.toUpperCase()}`);
    }
  };

  return (
    <div className={embedded ? "space-y-5" : "space-y-6"} data-testid="posvenda-comissoes-page">
      {!embedded && (
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="text-orange-500" size={26} />
              Comissões Pós-venda <span className="text-orange-500">3%</span>
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Gerada automaticamente ao emitir uma Ordem de Serviço.
            </p>
          </div>
        </header>
      )}

      {/* Botões export/refresh (sempre) */}
      <div className="flex items-center gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={() => exportar("csv")} data-testid="export-csv-btn">
          <FileSpreadsheet size={14} className="mr-1.5" /> CSV
        </Button>
        <Button variant="outline" size="sm" onClick={() => exportar("pdf")} data-testid="export-pdf-btn">
          <FileDown size={14} className="mr-1.5" /> PDF
        </Button>
        <Button variant="ghost" size="sm" onClick={carregar} data-testid="comissoes-refresh-btn">
          <RefreshCcw size={14} />
        </Button>
      </div>

      {/* KPIs — 4 cards (incluindo APROVADA) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpis-row">
        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-500">Total</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{brl(kpis.valor_total)}</div>
            <div className="text-xs text-slate-400 mt-1">{kpis.qtd_total || 0} comissões</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Clock size={12}/>Pendentes</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-700">{brl(kpis.valor_pendente)}</div>
            <div className="text-xs text-slate-400 mt-1">{kpis.qtd_pendente || 0} aguardando aprovação</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-sky-500">
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><ThumbsUp size={12}/>Aprovadas</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-sky-700">{brl(kpis.valor_aprovada)}</div>
            <div className="text-xs text-slate-400 mt-1">{kpis.qtd_aprovada || 0} aguardando pagamento</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><CheckCircle2 size={12}/>Pagas</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">{brl(kpis.valor_paga)}</div>
            <div className="text-xs text-slate-400 mt-1">{kpis.qtd_paga || 0} pagamentos</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar OS, proposta, vendedor ou cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
          data-testid="comissoes-busca"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="comissoes-filtro-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="pendente">Pendentes</SelectItem>
            <SelectItem value="aprovada">Aprovadas</SelectItem>
            <SelectItem value="paga">Pagas</SelectItem>
            <SelectItem value="cancelada">Canceladas</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-slate-500">
          {filtered.length} de {comissoes.length}
        </div>
      </div>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Carregando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-400" data-testid="comissoes-empty">
              Nenhuma comissão encontrada.
            </div>
          ) : (
            <Table data-testid="comissoes-table">
              <TableHeader>
                <TableRow>
                  <TableHead>OS / Proposta</TableHead>
                  <TableHead>Cliente</TableHead>
                  {isAdminViewer && <TableHead>Vendedor</TableHead>}
                  <TableHead className="text-right">Base</TableHead>
                  <TableHead className="text-right">Comissão (3%)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} data-testid={`comissao-row-${c.id}`}>
                    <TableCell>
                      <div className="font-mono text-sm font-semibold text-slate-800">{c.os_numero}</div>
                      <div className="text-xs text-slate-400 font-mono">{c.proposta_numero}</div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">{c.cliente_nome}</TableCell>
                    {isAdminViewer && <TableCell className="text-sm">{c.vendedor_nome || "—"}</TableCell>}
                    <TableCell className="text-right font-mono text-sm">{brl(c.base_calculo)}</TableCell>
                    <TableCell className="text-right font-mono text-sm font-bold text-orange-600">{brl(c.valor_comissao)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_LABELS[c.status]?.className || ""}>
                        {STATUS_LABELS[c.status]?.label || c.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {c.criado_em ? new Date(c.criado_em).toLocaleDateString("pt-BR") : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.proposta_id && (
                          <Link to={`/app/posvenda/propostas/${c.proposta_id}`}>
                            <Button size="sm" variant="ghost" title="Ver proposta">
                              <ExternalLink size={13} />
                            </Button>
                          </Link>
                        )}
                        {canAprovar && c.status === "pendente" && (
                          <Button size="sm" variant="outline"
                            className="text-sky-700 border-sky-300 hover:bg-sky-50"
                            onClick={() => abrirAcao("aprovar", c)}
                            data-testid={`btn-aprovar-${c.id}`}>
                            <ThumbsUp size={12} className="mr-1" /> Aprovar
                          </Button>
                        )}
                        {canPagar && c.status === "aprovada" && (
                          <Button size="sm" variant="outline"
                            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => abrirAcao("pagar", c)}
                            data-testid={`btn-pagar-${c.id}`}>
                            <CheckCircle2 size={12} className="mr-1" /> Pagar
                          </Button>
                        )}
                        {canCancelar && c.status !== "cancelada" && (
                          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                            onClick={() => abrirAcao("cancelar", c)}
                            data-testid={`btn-cancelar-${c.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal de confirmação */}
      <Dialog open={modal.open} onOpenChange={(v) => setModal((m) => ({ ...m, open: v }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {modal.acao === "aprovar" && <><ThumbsUp size={18} className="text-sky-600" /> Aprovar comissão</>}
              {modal.acao === "pagar" && <><CheckCircle2 size={18} className="text-emerald-600" /> Marcar como paga</>}
              {modal.acao === "cancelar" && <><XCircle size={18} className="text-red-600" /> Cancelar comissão</>}
            </DialogTitle>
          </DialogHeader>
          {modal.comissao && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 p-3 rounded space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">OS:</span><span className="font-mono">{modal.comissao.os_numero}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Vendedor:</span><span>{modal.comissao.vendedor_nome}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Valor:</span><span className="font-bold text-orange-600">{brl(modal.comissao.valor_comissao)}</span></div>
              </div>
              {modal.acao !== "cancelar" && (
                <Input
                  placeholder="Observação (opcional)"
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  data-testid="comissao-observacao-input"
                />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal((m) => ({ ...m, open: false }))}>Cancelar</Button>
            <Button onClick={confirmar} disabled={saving}
              className={
                modal.acao === "aprovar" ? "bg-sky-600 hover:bg-sky-700 text-white" :
                modal.acao === "pagar" ? "bg-emerald-600 hover:bg-emerald-700 text-white" :
                modal.acao === "cancelar" ? "bg-red-600 hover:bg-red-700 text-white" :
                ""
              }
              data-testid="comissao-confirmar-btn">
              {saving ? "..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
